import { createContext, useContext, useState, useCallback, type ReactNode } from 'react'

interface AiChatPanelContextType {
    isOpen: boolean
    /** Session ID to load when the panel opens */
    targetSessionId: string | null
    /** Message to auto-send once the session is loaded */
    pendingMessage: string | null
    /** Tab to switch to when panel opens ('agent' | 'user' | 'clara') */
    targetTab: string | null
    /** Context to inject into the session (e.g. IaC project summary for Clara) */
    injectedContext: string | null
    open: (options?: { sessionId?: string; message?: string; tab?: string; context?: string }) => void
    close: () => void
    toggle: () => void
    clearTargetSession: () => void
}

const AiChatPanelContext = createContext<AiChatPanelContextType | null>(null)

export function AiChatPanelProvider({ children }: { children: ReactNode }) {
    const [isOpen, setIsOpen] = useState(false)
    const [targetSessionId, setTargetSessionId] = useState<string | null>(null)
    const [pendingMessage, setPendingMessage] = useState<string | null>(null)
    const [targetTab, setTargetTab] = useState<string | null>(null)
    const [injectedContext, setInjectedContext] = useState<string | null>(null)

    // Support both old signature (sessionId, message) and new options object
    const open = useCallback((optionsOrSessionId?: string | { sessionId?: string; message?: string; tab?: string; context?: string }, message?: string) => {
        if (typeof optionsOrSessionId === 'object' && optionsOrSessionId !== null) {
            if (optionsOrSessionId.sessionId) setTargetSessionId(optionsOrSessionId.sessionId)
            if (optionsOrSessionId.message) setPendingMessage(optionsOrSessionId.message)
            if (optionsOrSessionId.tab) setTargetTab(optionsOrSessionId.tab)
            if (optionsOrSessionId.context) setInjectedContext(optionsOrSessionId.context)
        } else {
            if (optionsOrSessionId) setTargetSessionId(optionsOrSessionId)
            if (message) setPendingMessage(message)
        }
        setIsOpen(true)
    }, [])
    const close = useCallback(() => setIsOpen(false), [])
    const toggle = useCallback(() => setIsOpen((prev) => !prev), [])
    const clearTargetSession = useCallback(() => {
        setTargetSessionId(null)
        setPendingMessage(null)
        setTargetTab(null)
        setInjectedContext(null)
    }, [])

    return (
        <AiChatPanelContext.Provider value={{ isOpen, targetSessionId, pendingMessage, targetTab, injectedContext, open, close, toggle, clearTargetSession }}>
            {children}
        </AiChatPanelContext.Provider>
    )
}

export function useAiChatPanel() {
    const context = useContext(AiChatPanelContext)
    if (!context) {
        throw new Error('useAiChatPanel must be used within AiChatPanelProvider')
    }
    return context
}
